import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, FileText, SearchX } from "lucide-react";
import { Banner, Button, Card, Chip, DataTable, EmptyState, Toolbar, type ChipTone, type DataTableColumn } from "@/components/ds";
import BookingDetailDrawer from "@/components/BookingDetailDrawer";
import { apiRequest } from "@/lib/queryClient";
import { formatDubaiDate } from "@/lib/dubaiDate";
import {
  BookingStatus,
  transformBookingToUIDisplay,
  type BookingResponse,
  type BookingResponseUI,
} from "@/types/activities";

interface OrgBookingsProps {
  organizationId: string;
}

const STATUS: Record<string, { label: string; tone: ChipTone }> = {
  [BookingStatus.PendingVendorContact]: { label: "Submitted", tone: "warn" },
  [BookingStatus.VendorNegotiating]: { label: "Negotiating", tone: "info" },
  [BookingStatus.PendingOrganizationApproval]: { label: "Awaiting approval", tone: "info" },
  [BookingStatus.ApprovedAwaitingVendorBooking]: { label: "Booking vendor", tone: "info" },
  [BookingStatus.Confirmed]: { label: "Confirmed", tone: "ok" },
  [BookingStatus.Cancelled]: { label: "Cancelled", tone: "err" },
  [BookingStatus.NoVendorsAvailable]: { label: "No vendors", tone: "neutral" },
};

const statusOf = (row: BookingResponseUI) =>
  STATUS[row.booking.status] ?? { label: String(row.booking.status ?? "Unknown"), tone: "neutral" as ChipTone };

/** The server's placeholder when a booking's activity has no name. */
const UNKNOWN_ACTIVITY = "unknown activity";

const activityKnown = (r: BookingResponseUI) => {
  const name = r.booking.activityName?.trim();
  return !!name && name.toLowerCase() !== UNKNOWN_ACTIVITY;
};

/** The row's title: the activity, else "Booking <id>" — never the same placeholder on every row. */
export const bookingTitle = (r: BookingResponseUI) =>
  activityKnown(r) ? r.title : `Booking ${r.id.slice(0, 8)}`;

/** For a booking with no activity name: its preferred date and head count, so rows differ. */
export const bookingDetail = (r: BookingResponseUI) => {
  if (activityKnown(r)) return null;
  const people = r.booking.estimatedParticipantsCount;
  const date = formatDubaiDate(r.booking.preferredStartDate);
  return typeof people === "number" ? `${date} · ${people} ${people === 1 ? "person" : "people"}` : date;
};

const columns: DataTableColumn<BookingResponseUI>[] = [
  {
    id: "title",
    header: "Title",
    cell: (r) => {
      const detail = bookingDetail(r);
      return (
        <span>
          <b className="font-semibold text-ds-ink">{bookingTitle(r)}</b>
          {detail && <small className="block text-[12px] text-ds-ink2">{detail}</small>}
        </span>
      );
    },
  },
  {
    id: "status",
    header: "Status",
    cell: (r) => {
      const { label, tone } = statusOf(r);
      return <Chip tone={tone}>{label}</Chip>;
    },
  },
  { id: "submitted", header: "Submitted", cell: (r) => <span className="whitespace-nowrap">{formatDubaiDate(r.dateSubmitted)}</span> },
  { id: "vendor", header: "Vendor", cell: (r) => r.vendorName },
  {
    id: "price",
    header: "Price",
    align: "right",
    cell: (r) => <span className="whitespace-nowrap font-ds-mono">{r.vendorPrice ?? "—"}</span>,
  },
];

/**
 * Activity → Bookings: THIS company's bookings only, from the per-company list
 * `GET /bookings/organization/{id}` (W8 — the old tab read every company's bookings and broke on the
 * first one, B2). Rows go through `transformBookingToUIDisplay`; a row opens today's booking panel.
 * No bulk menu (it did nothing).
 */
export function OrgBookings({ organizationId }: OrgBookingsProps) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<BookingResponseUI | null>(null);
  const query = useQuery<BookingResponse[]>({
    queryKey: ["organizations", organizationId, "bookings"],
    queryFn: () => apiRequest<BookingResponse[]>("GET", `/bookings/organization/${organizationId}`),
    enabled: !!organizationId,
  });

  const rows = useMemo(
    () => (Array.isArray(query.data) ? query.data : []).map((b) => transformBookingToUIDisplay(b)),
    [query.data],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [bookingTitle(r), bookingDetail(r), r.vendorName, r.description, statusOf(r).label].some((v) =>
        v?.toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);

  return (
    <section className="space-y-3" data-testid="org-bookings">
      <h2 className="text-[15px] font-semibold text-ds-ink">Bookings</h2>
      {query.isError ? (
        <Banner
          tone="err"
          icon={AlertCircle}
          action={
            <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t load this company&apos;s bookings.
        </Banner>
      ) : (
        <Card>
          <Toolbar
            search={{ value: search, onChange: setSearch, placeholder: "Search bookings…", label: "Search bookings" }}
            count={query.isSuccess ? `${filtered.length} ${filtered.length === 1 ? "booking" : "bookings"}` : undefined}
          />
          <DataTable
            label="Bookings"
            columns={columns}
            rows={filtered}
            getRowId={(r) => r.id}
            onRowClick={setSelected}
            isLoading={query.isLoading}
            empty={
              rows.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No bookings yet"
                  text="Bookings this company makes from the marketplace show here."
                />
              ) : (
                <EmptyState
                  icon={SearchX}
                  title="Nothing matches this search."
                  action={{ label: "Clear search", onClick: () => setSearch("") }}
                />
              )
            }
          />
        </Card>
      )}
      <BookingDetailDrawer
        booking={selected?.booking ?? null}
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        onRefresh={() => void query.refetch()}
      />
    </section>
  );
}

export default OrgBookings;
